import { API_CONFIG, buildApiUrl, getAuthHeaders } from '../config/api.ts';
import { apiRequest } from '../utils/apiRequest.ts';

// Types for Event Reservations
export interface EventReservation {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  numberOfPersons: number;
  date: string;
  time: string;
  createdAt: string;
}

// Types for Work With Us
export interface WorkWithUs {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  currentEmployer: string | null;
  positionApplyingFor: string;
  message: string | null;
  createdAt: string;
}

export interface PaginatedEventReservationsResponse {
  data: EventReservation[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginatedWorkWithUsResponse {
  data: WorkWithUs[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Types for Sick Leave
export interface SickLeave {
  id: number;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  leaveStartDate: string;
  leaveEndDate: string;
  illnessInformation: string | null;
  doctorCertificatePath: string | null;
  createdAt: string;
}

export interface PaginatedSickLeaveResponse {
  data: SickLeave[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// Types for Open Table Reservations (large party requests, 15+ guests)
export interface OpenTableReservation {
  id: number;
  reservationDate: string;
  reservationTime: string;
  partySize: number;
  seatingPreference: string;
  occasion: string | null;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  email: string;
  specialRequests: string | null;
  marketingOptIn: boolean;
  smsConsent: boolean;
  createdAt: string;
}

export interface PaginatedOpenTableReservationsResponse {
  data: OpenTableReservation[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface WebsiteFormQueryParams {
  page?: number;
  limit?: number;
  email?: string;
}

// Website Forms API calls
export const websiteFormsApi = {
  // Get all event reservations with pagination and email search
  getEventReservations: async (
    params: WebsiteFormQueryParams = {},
    token: string
  ): Promise<PaginatedEventReservationsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.email) searchParams.append('email', params.email);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.WEBSITE_FORMS.EVENT_RESERVATIONS)}?${searchParams.toString()}`;
    
    return apiRequest<PaginatedEventReservationsResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get all work with us submissions with pagination and email search
  getWorkWithUs: async (
    params: WebsiteFormQueryParams = {},
    token: string
  ): Promise<PaginatedWorkWithUsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.email) searchParams.append('email', params.email);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.WEBSITE_FORMS.WORK_WITH_US)}?${searchParams.toString()}`;
    
    return apiRequest<PaginatedWorkWithUsResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get all sick leave submissions with pagination and email search
  getSickLeave: async (
    params: WebsiteFormQueryParams = {},
    token: string
  ): Promise<PaginatedSickLeaveResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.email) searchParams.append('email', params.email);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.WEBSITE_FORMS.SICK_LEAVE)}?${searchParams.toString()}`;

    return apiRequest<PaginatedSickLeaveResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },

  // Get all open table (large party) reservations with pagination and email search
  getOpenTableReservations: async (
    params: WebsiteFormQueryParams = {},
    token: string
  ): Promise<PaginatedOpenTableReservationsResponse> => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.email) searchParams.append('email', params.email);

    const url = `${buildApiUrl(API_CONFIG.ENDPOINTS.WEBSITE_FORMS.OPEN_TABLE_RESERVATIONS)}?${searchParams.toString()}`;

    return apiRequest<PaginatedOpenTableReservationsResponse>(
      url,
      {
        method: 'GET',
        headers: getAuthHeaders(token),
      }
    );
  },
};

